/**
 * Simple Authentication Manager
 * Phiên GỐC lưu localStorage (yody_auth) — bền 30 ngày.
 * Phiên IMPERSONATE ("xem dưới tài khoản F1") lưu sessionStorage (yody_impersonate) — TẠM THỜI theo tab:
 *   - Ưu tiên đọc trước localStorage → tab đang impersonate hoạt động dưới danh tính F1.
 *   - KHÔNG đụng localStorage → phiên gốc nguyên vẹn, đóng tab/mở lại là về tài khoản gốc (không bị kẹt).
 *   - Đi nhiều cấp (A → F1 → F2…): mỗi lần xem thêm 1 cấp, phiên đang xem được đẩy vào yody_imp_stack (sessionStorage)
 *     → stopImpersonation() lùi về đúng 1 cấp (anh Bình 27/10/2026); clearImpersonation() về thẳng tài khoản gốc.
 */

const AUTH_STORAGE_KEY = 'yody_auth';
const ACCESS_TOKEN_KEY = 'yody_access_token';
const IMPERSONATE_KEY = 'yody_impersonate';
const IMP_STACK_KEY = 'yody_imp_stack';

class AuthManager {
    /** Phiên impersonate hiện tại (sessionStorage) hoặc null */
    static getImpersonation() {
        try {
            const d = sessionStorage.getItem(IMPERSONATE_KEY);
            return d ? JSON.parse(d) : null;
        } catch (error) {
            return null;
        }
    }

    static isImpersonating() {
        const imp = this.getImpersonation();
        return !!(imp && imp.userInfo);
    }

    /** Các phiên impersonate cấp trên đang chờ quay lại (cũ → mới); [] khi chỉ xem 1 cấp */
    static getImpersonationStack() {
        try {
            const d = sessionStorage.getItem(IMP_STACK_KEY);
            return d ? JSON.parse(d) : [];
        } catch (error) {
            return [];
        }
    }

    /** Lưu phiên impersonate vào sessionStorage (tạm thời, theo tab) — KHÔNG ghi localStorage. Đang xem 1 cấp mà xem tiếp → cấp hiện tại vào ngăn xếp */
    static saveImpersonation(userInfo, accessToken) {
        try {
            const dang = this.getImpersonation();
            if (dang && dang.userInfo) {
                sessionStorage.setItem(IMP_STACK_KEY, JSON.stringify([...this.getImpersonationStack(), dang].slice(-20)));
            }
            sessionStorage.setItem(IMPERSONATE_KEY, JSON.stringify({ userInfo, accessToken, timestamp: Date.now() }));
            return true;
        } catch (error) {
            console.error('❌ AuthManager - Failed to save impersonation:', error);
            return false;
        }
    }

    /** Lùi 1 cấp: còn phiên trong ngăn xếp → về phiên đó; hết → về phiên gốc (localStorage) */
    static stopImpersonation() {
        try {
            const stack = this.getImpersonationStack();
            const truoc = stack.pop();
            if (truoc) {
                sessionStorage.setItem(IMPERSONATE_KEY, JSON.stringify(truoc));
                sessionStorage.setItem(IMP_STACK_KEY, JSON.stringify(stack));
            } else {
                sessionStorage.removeItem(IMPERSONATE_KEY);
                sessionStorage.removeItem(IMP_STACK_KEY);
            }
            return true;
        } catch (error) {
            return false;
        }
    }

    /** Về thẳng tài khoản gốc, bỏ hết các cấp đang xem */
    static clearImpersonation() {
        try {
            sessionStorage.removeItem(IMPERSONATE_KEY);
            sessionStorage.removeItem(IMP_STACK_KEY);
            return true;
        } catch (error) {
            return false;
        }
    }

    /**
     * Save user session after login (phiên GỐC → localStorage)
     */
    static saveSession(userInfo, accessToken) {
        try {
            const sessionData = {
                userInfo,
                accessToken,
                timestamp: Date.now()
            };
            localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(sessionData));
            if (accessToken) {
                localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
            }
            console.log('✅ AuthManager - Session saved successfully');
            return true;
        } catch (error) {
            console.error('❌ AuthManager - Failed to save session:', error);
            return false;
        }
    }

    /**
     * Get current session — ưu tiên phiên impersonate (sessionStorage), rồi mới tới phiên gốc (localStorage)
     */
    static getSession() {
        try {
            const imp = this.getImpersonation();
            if (imp && imp.userInfo) {
                return { ...imp, isImpersonating: true };
            }
            const sessionData = localStorage.getItem(AUTH_STORAGE_KEY);
            if (!sessionData) {
                return null;
            }
            return JSON.parse(sessionData);
        } catch (error) {
            console.error('❌ AuthManager - Failed to get session:', error);
            return null;
        }
    }

    /**
     * Get access token — token impersonate nếu đang impersonate, ngược lại token gốc
     */
    static getAccessToken() {
        try {
            const imp = this.getImpersonation();
            if (imp && imp.accessToken) {
                return imp.accessToken;
            }
            return localStorage.getItem(ACCESS_TOKEN_KEY);
        } catch (error) {
            console.error('❌ AuthManager - Failed to get access token:', error);
            return null;
        }
    }

    /**
     * Check if user is authenticated
     */
    static isAuthenticated() {
        const session = this.getSession();
        if (!session || !session.userInfo) {
            return false;
        }
        if (this.isSessionExpired()) {
            console.log('⚠️ AuthManager - Session expired, clearing');
            this.clearSession();
            return false;
        }
        return true;
    }

    /**
     * Get user info
     */
    static getUserInfo() {
        const session = this.getSession();
        return session?.userInfo || null;
    }

    /**
     * Update user info — cập nhật vào store đang active (impersonate hoặc gốc)
     */
    static updateUserInfo(userInfo) {
        const imp = this.getImpersonation();
        if (imp && imp.userInfo) {
            imp.userInfo = { ...imp.userInfo, ...userInfo };
            sessionStorage.setItem(IMPERSONATE_KEY, JSON.stringify(imp));
            return true;
        }
        const session = this.getSession();
        if (session) {
            session.userInfo = { ...session.userInfo, ...userInfo };
            localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
            console.log('✅ AuthManager - User info updated');
            return true;
        }
        return false;
    }

    /**
     * Clear session (logout) — xoá cả phiên gốc lẫn impersonate
     */
    static clearSession() {
        try {
            localStorage.removeItem(AUTH_STORAGE_KEY);
            localStorage.removeItem(ACCESS_TOKEN_KEY);
            sessionStorage.removeItem(IMPERSONATE_KEY);
            sessionStorage.removeItem(IMP_STACK_KEY);
            console.log('✅ AuthManager - Session cleared');
            return true;
        } catch (error) {
            console.error('❌ AuthManager - Failed to clear session:', error);
            return false;
        }
    }

    /**
     * Check if session is expired. Phiên impersonate (token 15', theo tab) KHÔNG áp mốc 30 ngày.
     */
    static isSessionExpired() {
        if (this.isImpersonating()) {
            return false;
        }
        const sessionData = localStorage.getItem(AUTH_STORAGE_KEY);
        if (!sessionData) return true;
        let session = null;
        try { session = JSON.parse(sessionData); } catch (e) { return true; }
        if (!session || !session.timestamp) {
            return true;
        }

        // Session expires after 30 days
        const EXPIRY_TIME = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
        const now = Date.now();
        return (now - session.timestamp) > EXPIRY_TIME;
    }

    /**
     * Refresh session timestamp — chỉ phiên gốc (localStorage), không động phiên impersonate
     */
    static refreshSession() {
        if (this.isImpersonating()) {
            return false;
        }
        const sessionData = localStorage.getItem(AUTH_STORAGE_KEY);
        if (!sessionData) return false;
        try {
            const session = JSON.parse(sessionData);
            session.timestamp = Date.now();
            localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
            return true;
        } catch (e) {
            return false;
        }
    }
}

export default AuthManager;
